import { makeAutoObservable, runInAction } from 'mobx';

import { cmApi } from '@src/api';
import type { CmApi, EtlLogDto, EtlProcessDto, EtlTypeDto } from '@src/api';

import { preloaderStore } from '../preloaderStore';

export type EtlLogDtoDefId = Omit<EtlLogDto, 'id'>;
export type LogsType = EtlProcessDto & EtlLogDtoDefId & { children?: EtlLogDtoDefId[] } & { id: number | string };

type AdditionalOptionsType = {
  guid: string;
  reportPackageCode: string;
  reportVersions?: string;
  controlVersions?: string;
  exportFNS?: string;
};

type FilterValues = {
  dateFrom?: string;
  dateTo?: string;
  guid?: string;
};

class LogsStore {
  logs: LogsType[] = [];

  isImportStarted = false;

  etlTypes: EtlTypeDto[] = [];

  importText = '';

  activeEtlProcesses: EtlProcessDto[] = [];

  expandedKeys: string[] = [];

  additionalOptions: AdditionalOptionsType | null = null;

  filterValues: FilterValues = {};

  etlTypeFilter: string | undefined = undefined;

  api: CmApi;

  constructor({ api }: { api: CmApi }) {
    this.api = api;
    makeAutoObservable(this);
  }

  setAdditionalOptions = (value: AdditionalOptionsType | null) => {
    this.additionalOptions = value;
  };

  setFilterValues = (values: FilterValues, clear?: boolean) => {
    this.filterValues = clear ? {} : { ...this.filterValues, ...values };
  };

  setExpandedKeys = (keys: string[]) => {
    this.expandedKeys = keys;
  };

  setEtlTypeFilter = (value: string) => {
    this.etlTypeFilter = value;
  };

  getActiveProcesses = async () => {
    const { data } = await this.api.etlProcesses.getActiveEtlProcesses();
    runInAction(() => {
      if (!!data.length) {
        this.isImportStarted = true;
        this.activeEtlProcesses = data;
      }
    });
  };

  checkActiveProcesses = async () => {
    const { data } = await this.api.etlProcesses.getActiveEtlProcesses();
    const finishedProcesses = this.activeEtlProcesses.filter((proc) => !data.some((pr) => pr.guid === proc.guid));

    runInAction(() => {
      if (finishedProcesses.length > 0) {
        this.activeEtlProcesses = this.activeEtlProcesses.filter(
          (proc) => !finishedProcesses.some((pr) => pr.guid === proc.guid),
        );
      }
      if (data.length === 0) this.isImportStarted = false;
    });
  };

  getEtlTypes = async () => {
    preloaderStore.preloader(true);

    const { data } = await this.api.etlProcesses.getEtlTypes();
    runInAction(() => {
      if (data) {
        this.etlTypes = data;
      }
    });
    preloaderStore.preloader(false);
  };

  getEtlProcessByGuid = async (etlGuid: string) => {
    preloaderStore.preloader(true);
    const { data } = await this.api.etlProcesses.getEtlProcessByGuid(etlGuid);
    preloaderStore.preloader(false);
    return data;
  };

  clear = () => {
    this.logs = [];
    this.isImportStarted = false;
    this.importText = '';
    this.etlTypes = [];
    this.activeEtlProcesses = [];
    this.expandedKeys = [];
    this.additionalOptions = null;
    this.filterValues = {};
    this.etlTypeFilter = undefined;
  };
}

export const logsStore = new LogsStore({ api: cmApi });
